'use client';

import { Locale } from '@tuic/i18n';
import { ComponentBlock } from '@modules/prism-client/types/types';
import {
  DashboardCard,
  DashboardCardProps,
} from '@tuic/web-compositions/shared/DashboardCard';
import {
  ExplanationVideoOptions,
  WithExplanationVideoDialog,
} from '../../../../../app/[locale]/[bookingCode]/_components/with-explanation-video';
import PrismAPI from '@modules/prism-client/types/prism-types';
import { InternetCard } from '@modules/get-slash-internet-card';
import { ChatCard } from '../../../../../app/[locale]/_components/chat-card';
import { Link } from '../../elements/Link';
import { NewInformationTag } from './NewInformationTag';
import { ComponentPropsWithoutRef } from 'react';

export type CardBlockData = {
  card?: DashboardCardProps;
  link?: PrismAPI.LinkElement;
  explanationVideo?: ExplanationVideoOptions;
  internetCard?: ComponentPropsWithoutRef<typeof InternetCard>;
  chatCard?: PrismAPI.CardChatCard;
  newInformationTag?: PrismAPI.CardNewInformationTag;
  locale?: Locale;
};

export const CardBlock: ComponentBlock<CardBlockData> = ({
  data,
  fallback,
}) => {
  if (fallback) {
    return <DashboardCard fallback />;
  }

  const {
    link,
    explanationVideo,
    internetCard,
    chatCard,
    newInformationTag,
    locale,
  } = data ?? {};

  const card = createCardWithNewInformationTag(
    locale,
    data?.card,
    newInformationTag,
  );

  if (explanationVideo) {
    return (
      <WithExplanationVideoDialog {...explanationVideo}>
        {open => (
          <DashboardCard {...card} asChild>
            <button onClick={open} />
          </DashboardCard>
        )}
      </WithExplanationVideoDialog>
    );
  }

  if (internetCard) {
    return <InternetCard {...internetCard} dashboardCard={card} />;
  }

  if (chatCard) {
    if (link) {
      return (
        <ChatCard {...chatCard} dashboardCard={{
          ...card,
          asChild: true,
          children: <Link data={link} />
        }} />
      );
    }

    return <ChatCard {...chatCard} dashboardCard={card} />;
  }

  if (link) {
    return (
      <DashboardCard {...card} asChild>
        <Link data={link} />
      </DashboardCard>
    );
  }

  return <DashboardCard {...card} />;
};

/**
 * Create a card with NewInformationTag if newInformationTag data exists
 */
const createCardWithNewInformationTag = (
  locale: Locale | undefined,
  card: DashboardCardProps | undefined,
  newInformationTag: PrismAPI.CardNewInformationTag | undefined,
): DashboardCardProps | undefined => {
  if (!card || !newInformationTag || !locale) {
    return card;
  }

  return {
    ...card,
    tag: (
      <NewInformationTag
        newInformationTag={newInformationTag}
        originalTag={card.tag}
        locale={locale}
      />
    ),
  };
};
